import {FC, useState} from 'react'
import {Link} from 'react-router-dom'
import styled from '@emotion/styled'
import {KTSVG, toAbsoluteUrl} from '../../../_metronic/helpers'
import {useUsermenuData} from '../../hooks/useUserMenuData'
import {useSidebar} from './SidebarContext'
import {SidebarMenu} from './SidebarMenu'

const NAVY = '#00335d'
const NAVY_DARKER = '#002c50'
const ACCENT = '#009ef7'
const WIDTH = 265
const WIDTH_MIN = 75
const BREAKPOINT = 992

const Styled = styled.aside`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 110;
  display: flex;
  flex-direction: column;
  width: ${WIDTH}px;
  background-color: ${NAVY};
  transition: width 0.3s ease, transform 0.3s ease;

  .sb-logo {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    height: 64px;
    padding: 0 20px;
    background-color: ${NAVY_DARKER};

    img.wide {
      height: 26px;
    }
    img.mini {
      display: none;
      height: 32px;
    }
  }

  .sb-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.55);
    transition: color 0.2s ease, background-color 0.2s ease, transform 0.3s ease;

    &:hover {
      color: #fff;
      background-color: rgba(255, 255, 255, 0.08);
    }
  }

  .sb-scroll {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px 12px 24px;

    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.15);
      border-radius: 4px;
    }
  }

  /* ---------- itens do menu ---------- */
  .sb-link {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 12px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: rgba(255, 255, 255, 0.82);
    font-size: 13px;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover {
      color: #fff;
      background-color: rgba(255, 255, 255, 0.06);
    }

    &.active {
      color: #fff;
      background-color: ${ACCENT};
    }

    &.here {
      color: #fff;
    }
  }

  .sb-label {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sb-icon {
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.65);
  }

  .sb-link.active .sb-icon {
    color: #fff;
  }

  .sb-home {
    margin-bottom: 4px;
  }

  /* ---------- seções ---------- */
  .sb-section {
    margin-top: 18px;
  }

  .sb-section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 12px 8px;
  }

  .sb-section-title {
    color: rgba(255, 255, 255, 0.45);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ---------- acordeão ---------- */
  .sb-chevron {
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.45);
    transition: transform 0.2s ease;
  }

  .sb-accordion.open > .sb-accordion-toggle .sb-chevron {
    transform: rotate(180deg);
  }

  .sb-accordion-body {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.25s ease;

    > .sb-accordion-inner {
      overflow: hidden;
      min-height: 0;
    }
  }

  .sb-accordion.open > .sb-accordion-body {
    grid-template-rows: 1fr;
  }

  .sb-link-sub {
    padding-left: 24px;
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.68);
  }

  /* ---------- desktop: minimizado ---------- */
  @media (min-width: ${BREAKPOINT}px) {
    .sb-close {
      display: none;
    }

    &.collapsed {
      width: ${WIDTH_MIN}px;

      .sb-logo {
        justify-content: center;
        padding: 0;

        img.wide {
          display: none;
        }
        img.mini {
          display: block;
        }
        .sb-btn {
          display: none;
        }
      }

      .sb-label,
      .sb-section-title,
      .sb-chevron {
        display: none;
      }

      /* no rail ficam apenas o home e um ícone por seção */
      .sb-section-body {
        display: none;
      }

      .sb-section {
        margin-top: 6px;
      }

      .sb-link,
      .sb-section-header {
        justify-content: center;
        padding: 10px 0;
        border-radius: 8px;
        cursor: pointer;
      }

      .sb-section-header:hover {
        background-color: rgba(255, 255, 255, 0.06);

        .sb-icon {
          color: #fff;
        }
      }
    }

    &.minimized .sb-toggle .svg-icon {
      transform: rotate(180deg);
    }
  }

  /* ---------- tablet/mobile: drawer ---------- */
  @media (max-width: ${BREAKPOINT - 0.02}px) {
    transform: translateX(-105%);
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.3);

    &.drawer-open {
      transform: translateX(0);
    }

    .sb-toggle {
      display: none;
    }
  }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 109;
  background: rgba(0, 0, 0, 0.35);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;

  &.show {
    opacity: 1;
    pointer-events: auto;
  }

  @media (min-width: ${BREAKPOINT}px) {
    display: none;
  }
`

const Sidebar: FC = () => {
  const {menuData} = useUsermenuData()
  const {isDrawerOpen, closeDrawer, isMinimized, toggleMinimized} = useSidebar()

  // Hover-expand controlado por estado: ao clicar em "recolher" o sidebar
  // fecha na hora (mesmo com o mouse em cima) e o hover só volta a expandir
  // depois que o mouse sai e entra de novo.
  const [isHovered, setIsHovered] = useState(false)
  const [hoverArmed, setHoverArmed] = useState(true)
  const isCollapsed = isMinimized && !(isHovered && hoverArmed)

  const handleToggle = () => {
    setHoverArmed(false)
    toggleMinimized()
  }

  return (
    <>
      <Backdrop className={isDrawerOpen ? 'show' : ''} onClick={closeDrawer} />
      <Styled
        className={`${isMinimized ? 'minimized' : ''} ${isCollapsed ? 'collapsed' : ''} ${
          isDrawerOpen ? 'drawer-open' : ''
        }`}
        aria-label='Menu lateral'
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false)
          setHoverArmed(true)
        }}
        onClick={() => {
          // clicar no rail recolhido expande o menu
          if (isCollapsed) {
            setIsHovered(true)
            setHoverArmed(true)
          }
        }}
      >
        <div className='sb-logo'>
          <Link to='/'>
            <img className='wide' alt='Insight Experience' src={toAbsoluteUrl('/media/logos/logo-wide.png')} />
            <img className='mini' alt='Insight Experience' src={toAbsoluteUrl('/media/logos/logo.png')} />
          </Link>
          <button
            type='button'
            className='sb-btn sb-toggle'
            title='Minimizar menu'
            onClick={handleToggle}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr080.svg' className='svg-icon-2 rotate-180' />
          </button>
          <button type='button' className='sb-btn sb-close' title='Fechar menu' onClick={closeDrawer}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </button>
        </div>

        <div className='sb-scroll'>
          <SidebarMenu items={menuData} />
        </div>
      </Styled>
    </>
  )
}

export {Sidebar}
